import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AccountManagerService } from 'account-manager/services/AccountManagerService';

import {
  Form,
  FormControl,
  FormGroup,
  PasswordFormField,
  ShortTextFormField
} from '@client:common/form';
import { Validators } from '@client:common/form/validation';
import { Button } from '@client:common/button';
import { Ripple } from '@client:common/effects/ripple';
import { Markdown } from '@client:common/Markdown';
import { ProgressIndicator } from '@client:common/overlay/progress-indicator';
import { Notification } from '@client:common/overlay/notification';

import './PasswordReset.dark.scss';
import './PasswordReset.light.scss';

interface Props {
  onSubmit: (
    username: string,
    verificationCode: string,
    password: string
  ) => void;
}

export function PasswordReset(props: Props) {
  const [searchParams] = useSearchParams();
  const email = new URLSearchParams(searchParams).get('email') ?? '';
  const passwordControlRef = useRef(
    new FormControl('', [Validators.checkPassword()])
  );
  const formGroupRef = useRef(
    new FormGroup({
      verificationCode: new FormControl('', [Validators.checkNotEmpty()]),
      password: passwordControlRef.current
    })
  );
  const [isFormValid, setIsFormValid] = useState(
    formGroupRef.current.isValid()
  );

  const message = [
    `We just sent a code to \n**${email}**.<br />`,
    'This email could take up to 30 minutes to arrive.<br />Feel free to come back later.'
  ].join(' ');

  useEffect(() => {
    const subscription = passwordControlRef.current.valueChanges().subscribe();
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    const subscription = formGroupRef.current.validityChanges().subscribe({
      next: setIsFormValid
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const onSendNewCode = () => {
    const accountManagerService = AccountManagerService.getInstance();

    ProgressIndicator.show();
    accountManagerService
      .sendPasswordRecoveryEmail(email)
      .then(() => {
        Notification.open(
          <Markdown>{`New code has been sent to \n**${email}**.`}</Markdown>
        );
      })
      .catch(() => {
        Notification.open(
          <Markdown>
            There was a problem sending a password recovery email
          </Markdown>
        );
      })
      .finally(() => {
        ProgressIndicator.hide();
      });
  };

  const onSubmit = () => {
    const formValue = formGroupRef.current.getValue();
    const cognitoUsername = email.split('@')[0];
    props.onSubmit(
      cognitoUsername,
      formValue.verificationCode,
      formValue.password
    );
  };

  if (email.length === 0 || !email.includes('@')) {
    return null;
  }

  return (
    <div className='password-reset'>
      <header>
        <h2>Forgot Your Password?</h2>
        <Markdown className='markdown-message'>{message}</Markdown>
      </header>
      <Form
className='password_collector'
formGroup={formGroupRef.current}>
        <ShortTextFormField
          label='Verification code'
          formControl={formGroupRef.current.findControl('verificationCode')}
        />
        <Button
          style='basic'
          color='primary'
          className='new-code_trigger'
          onClick={onSendNewCode}
        >
          <Ripple />
          SEND ME A NEW CODE
        </Button>
        <PasswordFormField
          label='New Password'
          showPasswordHint
          formControl={formGroupRef.current.findControl('password')}
        />
        <Button
          style='raised'
          color='primary'
          disabled={!isFormValid || formGroupRef.current.isPristine()}
          onClick={onSubmit}
        >
          <Ripple />
          RESET PASSWORD
        </Button>
      </Form>
    </div>
  );
}
